import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import useAuthStore from "../store/authStore";

export default function FlightBookingForm() {
  const { user, token } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const initialState = {
    full_name: user ? user.name : "",
    email: user ? user.email : "",
    phone: "",
    departure: "",
    destination: "",
    departure_date: "",
    return_date: "",
    trip_type: "round_trip",
    adults: '',
    children: '',
    infants: '',
    preferred_airline: "",
    cabin_class: "Economy",
    notes: "",
  };

  const [form, setForm] = useState(initialState);

  // When user logs in later, auto-update form
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        full_name: user.name,
        email: user.email,
      }));
    }
  }, [user]);

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const resp = await axios.post(
        "https://backend.ysskytravel.com/api/flights/request",
        form,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }
      );

      setSuccess("Your flight quote request has been sent successfully!");

      // RESET FORM, but keep logged-in user data
      setForm({
        ...initialState,
        full_name: user ? user.name : "",
        email: user ? user.email : "",
      });

    } catch (err) {
      setError(err?.response?.data?.error || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="max-w-3xl mx-auto bg-white/90 backdrop-blur-md p-6 mb-6 md:p-8 rounded-2xl shadow-xl border border-green-200"
    >
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-primary drop-shadow-sm">
        Flight Booking Request
      </h2>

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-xl text-green-700 shadow-sm animate-pulse text-sm md:text-base">
          {success}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-xl text-red-700 shadow-sm text-sm md:text-base">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">

        {/* ONLY show Name & Email if user is NOT logged in */}
        {!user && (
          <>
            <input
              name="full_name"
              placeholder="Full Name"
              value={form.full_name}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400 focus:border-green-500"
            />

            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={form.email}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400 focus:border-green-500"
            />
          </>
        )}

        <input
          name="phone"
          placeholder="Phone Number"
          value={form.phone}
          onChange={handleChange}
          required
          className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
        />

        <input
          name="preferred_airline"
          placeholder="Preferred Airline"
          value={form.preferred_airline}
          onChange={handleChange}
          className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
        />

        <input
          name="departure"
          placeholder="Departure Airport / City"
          value={form.departure}
          onChange={handleChange}
          className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
        />

        <input
          name="destination"
          placeholder="Destination Airport / City"
          value={form.destination}
          onChange={handleChange}
          className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
        />

        <input
          type="date"
          name="departure_date"
          value={form.departure_date}
          onChange={handleChange}
          className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
        />

        <input
          type="date"
          name="return_date"
          value={form.return_date}
          disabled={form.trip_type !== "round_trip"}
          onChange={handleChange}
          className={`w-full p-3 border rounded-xl shadow-sm transition-all ${
            form.trip_type !== "round_trip"
              ? "bg-gray-100 cursor-not-allowed"
              : "bg-white"
          } focus:ring-2 focus:ring-green-400 focus:border-green-500`}
        />

        <select
          name="trip_type"
          value={form.trip_type}
          onChange={handleChange}
          className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
        >
          <option value="round_trip">Round Trip</option>
          <option value="one_way">One Way</option>
          <option value="multi_city">Multi-City</option>
        </select>

        <select
          name="cabin_class"
          value={form.cabin_class}
          onChange={handleChange}
          className="w-full p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
        >
          <option>Economy</option>
          <option>Premium Economy</option>
          <option>Business</option>
          <option>First</option>
        </select>

        {/* PASSENGERS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 col-span-1 md:col-span-2">
          <input
            type="number"
            name="adults"
            min={1}
            placeholder="Adults"
            value={form.adults}
            onChange={handleChange}
            className="p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
          />

          <input
            type="number"
            name="children"
            min={0}
            placeholder="Children"
            value={form.children}
            onChange={handleChange}
            className="p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
          />

          <input
            type="number"
            name="infants"
            min={0}
            placeholder="Infants"
            value={form.infants}
            onChange={handleChange}
            className="p-3 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
          />
        </div>

        <textarea
          name="notes"
          placeholder="Additional Notes (optional)"
          value={form.notes}
          onChange={handleChange}
          className="col-span-1 md:col-span-2 w-full p-3 h-24 border rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-green-400"
        />

        <div className="col-span-1 md:col-span-2 flex justify-center mt-2">
          <motion.button
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={loading}
            className="bg-primary text-white px-8 py-3 w-full sm:w-auto rounded-xl font-semibold shadow-lg"
          >
            {loading ? "Sending..." : "Send Request"}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
}
